import { AlertIcon } from '@approvalmax/ui';
import { Alert, Grid, Popup, Table, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import moment from 'moment';
import { memo, ReactNode, useMemo } from 'react';
import { AccountingDomainEntitiesBudgetCheckStatus } from 'shared/data/webApp/v2';

import { QBooksBudgetImpactBreakdowns } from '../QBooksBudgetImpactBreakdowns/QBooksBudgetImpactBreakdowns';
import { getColumnDefinitions } from './QBooksBudgetImpactDetailsPopupContent.config';
import { messages } from './QBooksBudgetImpactDetailsPopupContent.messages';
import { QBooksBudgetImpactDetailsPopupContentProps } from './QBooksBudgetImpactDetailsPopupContent.types';

export const QBooksBudgetImpactDetailsPopupContent = memo<QBooksBudgetImpactDetailsPopupContentProps>((props) => {
    const { data, budgetCurrency, request } = props;

    const lineItems = useMemo(() => {
        return [...request.details.accountLineItems, ...request.details.lineItems].filter((line) =>
            data?.lineItemIds.includes(line.id)
        );
    }, [request.details, data?.lineItemIds]);

    const columnDefinitions = useMemo(
        () => getColumnDefinitions(request.currency, request.details.lineAmountType, data?.accountName),
        [request.currency, request.details.lineAmountType, data?.accountName]
    );

    const budgetPeriod = useMemo(() => {
        if (!data) {
            return '';
        }

        return `${moment.utc(data.budgetPeriodStart.asDateTime).format('MMM YYYY')} - ${moment.utc(data.budgetPeriodEnd.asDateTime).format('MMM YYYY')}`;
    }, [data]);

    if (!data) {
        return null;
    }

    return (
        <>
            <Popup.Header title={messages.title({ budgetName: data.budgetName })} />

            <Popup.Body>
                <Grid rowGap={24}>
                    <Grid rowGap={16}>
                        <Text font='body' fontSize='small'>
                            {messages.note({
                                requestFriendlyName: request.displayName,
                                budgetName: data.budgetName,
                                category: data.accountName,
                                period: budgetPeriod,
                                b: (chunks: ReactNode[]) => <b>{chunks}</b>,
                            })}
                        </Text>

                        {data.budgetCheckStatus !== AccountingDomainEntitiesBudgetCheckStatus.Ok && (
                            <Alert
                                invalid={data.budgetCheckStatus === AccountingDomainEntitiesBudgetCheckStatus.Warning}
                                color={
                                    data.budgetCheckStatus === AccountingDomainEntitiesBudgetCheckStatus.Warning
                                        ? 'midnight20'
                                        : 'red80'
                                }
                                size='small'
                                startIcon={<AlertIcon />}
                            >
                                {messages.warningOfExceed}
                            </Alert>
                        )}

                        <Table columns={columnDefinitions} data={lineItems} headerColor='white100' hideEmptyColumns />

                        <Grid gridTemplateColumns='auto 180px'>
                            <Text font='body' fontSize='small' color='midnight70' textAlign='right'>
                                {messages.totalBudgetImpact({
                                    currency: budgetCurrency,
                                })}
                            </Text>

                            <Text
                                font='label'
                                fontSize='medium'
                                fontWeight='medium'
                                textAlign='right'
                                spacing='0 8 0 0'
                            >
                                {intl.formatNumber(data.linesAmount)}
                            </Text>
                        </Grid>
                    </Grid>

                    <QBooksBudgetImpactBreakdowns
                        breakdowns={data.budgetBreakdowns}
                        budgetAmounts={data.budgetAmounts}
                        budgetPeriod={budgetPeriod}
                        budgetCheckPeriod={data.budgetCheckPeriod}
                        budgetCurrency={budgetCurrency}
                        requestFriendlyName={request.displayName}
                    />
                </Grid>
            </Popup.Body>
        </>
    );
});

QBooksBudgetImpactDetailsPopupContent.displayName = 'QBooksBudgetImpactDetailsPopup';
